import { IVA_RATE } from '@repo/shared-types';
import { Button, FormDrawer, Input, Label, Switch, Textarea } from '@repo/ui';
import { Plus, Trash2 } from 'lucide-react';
import type * as React from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useCurrentUser } from '@/features/auth/hooks/useAuthMutations';
import { useProduct } from '@/features/catalog/api/products';
import { useClient } from '@/features/crm/api/clients';
import { useCreateQuotation } from '@/features/sales/api/quotations';
import type {
  CreateQuotationFormValues,
  QuotationItemFormValues,
} from '@/features/sales/types/quotation.types';
import { ClientPicker, CurrencySelect, ProductPicker, SellerSelect } from './pickers';

/**
 * QuotationForm — controlled-state form for sales quotations.
 *
 * Why controlled state (NOT TanStack Form field arrays): the items
 * list is dynamic — the user can add/remove rows interactively.
 * TS strict + TanStack Form's `useFieldArray` typing broke on
 * Slice 0 PR#4 (obs-291). Plain `useState` plus Zod validation on
 * submit sidesteps the issue and keeps the types local.
 *
 * Subtotal auto-calc: each row's `subtotal` is computed live from
 * `unitPrice * quantity` so the FE shows a running total before
 * the POST. The BE re-computes and persists it authoritatively.
 *
 * Picker UX (Slice 9 follow-up):
 *   - Cliente: dropdown buscable (autocomplete por nombre). El wire
 *     payload sigue enviando `clientId` (UUID); el valor mostrado
 *     es el nombre resuelto.
 *   - Vendedor: dropdown estático de usuarios con rol SELLER en el
 *     tenant actual (≈10 — ver `?role=SELLER` en la ruta users).
 *   - Moneda: dropdown estático de monedas activas (USD + VES).
 *   - Producto: dropdown buscable (autocomplete por nombre/SKU).
 *
 * Resolución de ids pre-cargados: cuando el form se abre vía el
 * botón "Enviar a cotización" del CalculatorModal el padre sólo
 * provee `productId` (no el nombre). Los pickers consultan el
 * registro vía `useProduct` / `useClient` para que el input
 * muestre el label humano en vez del UUID crudo.
 */
export interface QuotationFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pre-fill values (Slice 8 PR#1) — used by the CalculatorModal's
   * "Enviar a cotización" action. When omitted the form uses the
   * existing empty defaults. `items` are seeded with fresh client-side
   * keys for React rendering.
   */
  initialValues?: Partial<CreateQuotationFormValues>;
}

interface QuotationItemRow extends QuotationItemFormValues {
  /** Stable client-side key for React rendering. */
  _key: string;
  /** Local display label for the resolved product (not sent on the wire). */
  productName: string;
}

const EMPTY_ITEM: QuotationItemRow = {
  _key: crypto.randomUUID(),
  productId: '',
  productName: '',
  quantity: '1',
  unitPrice: '0',
  subtotal: '0',
  notes: null,
};

type FormValues = {
  clientId: string;
  clientName: string;
  sellerId: string;
  currencyId: string;
  includesTax: boolean;
  validUntil: string | null;
  notes: string | null;
  isFinanced: boolean;
  totalInstallments: number | undefined;
  downPayment: string;
  startDate: string | null;
  items: QuotationItemRow[];
};

/**
 * Quotation default validity window — `today + 7 days`, formatted as
 * YYYY-MM-DD to match the `<input type="date">` value contract. The
 * drawer pre-fills this on a fresh open so the user does not have to
 * pick a date just to make the field valid; they can still clear or
 * override it. Computed at module load (cheap; not in a hook) because
 * the form's `DEFAULT_VALUES` is a frozen literal used by the
 * `useState` initializer.
 */
function defaultValidUntil(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

const DEFAULT_VALUES: FormValues = {
  clientId: '',
  clientName: '',
  sellerId: '',
  currencyId: '',
  includesTax: true,
  validUntil: defaultValidUntil(),
  notes: null,
  isFinanced: false,
  totalInstallments: undefined,
  downPayment: '',
  startDate: null,
  items: [EMPTY_ITEM],
};

/**
 * Merge optional `initialValues` over the empty defaults, giving each
 * seeded item a fresh client-side key. Returns the plain default when
 * no pre-fill is provided (preserves the pre-Slice-8 behaviour).
 */
function buildInitialValues(initialValues?: Partial<CreateQuotationFormValues>): FormValues {
  if (!initialValues) return DEFAULT_VALUES;
  const items: QuotationItemRow[] =
    initialValues.items && initialValues.items.length > 0
      ? initialValues.items.map((it) => ({
          ...it,
          productName: '',
          _key: crypto.randomUUID(),
        }))
      : DEFAULT_VALUES.items;
  return {
    clientId: initialValues.clientId ?? DEFAULT_VALUES.clientId,
    clientName: '',
    sellerId: initialValues.sellerId ?? DEFAULT_VALUES.sellerId,
    currencyId: initialValues.currencyId ?? DEFAULT_VALUES.currencyId,
    includesTax: initialValues.includesTax ?? DEFAULT_VALUES.includesTax,
    validUntil: initialValues.validUntil ?? DEFAULT_VALUES.validUntil,
    notes: initialValues.notes ?? DEFAULT_VALUES.notes,
    isFinanced: initialValues.isFinanced ?? DEFAULT_VALUES.isFinanced,
    totalInstallments: initialValues.totalInstallments ?? DEFAULT_VALUES.totalInstallments,
    downPayment: initialValues.downPayment ?? DEFAULT_VALUES.downPayment,
    startDate: initialValues.startDate ?? DEFAULT_VALUES.startDate,
    items,
  };
}

/**
 * Convert the loose local state into the wire-shape the BE expects.
 * The autocomplete/seller/currency pickers store the UUID only; any
 * transient label fields are stripped before POST.
 */
function toWireValues(values: FormValues): CreateQuotationFormValues {
  const { items, clientName: _cn, ...rest } = values;
  const wireItems = items.map(({ productName: _pn, _key: _k, ...it }) => it);
  return { ...rest, items: wireItems };
}

export function QuotationForm(props: QuotationFormProps): React.ReactElement {
  return <QuotationFormBody {...props} />;
}

function QuotationFormBody({
  open,
  onOpenChange,
  initialValues,
}: QuotationFormProps): React.ReactElement {
  const uid = useId();
  const [values, setValues] = useState<FormValues>(() => buildInitialValues(initialValues));
  const [error, setError] = useState<string | null>(null);
  const create = useCreateQuotation();

  // Re-seed from `initialValues` each time the drawer opens with a
  // pre-fill payload (CalculatorModal "Enviar a cotización" flow).
  useEffect(() => {
    if (open && initialValues) {
      setValues(buildInitialValues(initialValues));
      setError(null);
    }
  }, [open, initialValues]);

  const recomputeSubtotal = (item: QuotationItemFormValues): string => {
    const qty = Number(item.quantity || 0);
    const price = Number(item.unitPrice || 0);
    return (Number.isFinite(qty) && Number.isFinite(price) ? qty * price : 0).toFixed(4);
  };

  const setItem = (
    index: number,
    patch: Partial<QuotationItemRow> | ((current: QuotationItemRow) => Partial<QuotationItemRow>),
  ) => {
    setValues((prev) => {
      const items = prev.items.slice();
      const current = items[index];
      if (!current) return prev;
      // Functional patch is used by the product picker to preserve
      // an already-edited unitPrice when the user re-picks a product
      // (see `QuotationItemRowFields`).
      const resolved = typeof patch === 'function' ? patch(current) : patch;
      const merged = { ...current, ...resolved };
      merged.subtotal = recomputeSubtotal(merged);
      items[index] = merged;
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setValues((prev) => ({
      ...prev,
      items: [...prev.items, { ...EMPTY_ITEM, _key: crypto.randomUUID() }],
    }));
  };

  const removeItem = (index: number) => {
    setValues((prev) => {
      if (prev.items.length === 1) return prev;
      const items = prev.items.slice();
      items.splice(index, 1);
      return { ...prev, items };
    });
  };

  const validate = (): string | null => {
    if (!values.clientId) return 'Elegí un cliente';
    if (!values.sellerId) return 'Elegí un vendedor';
    if (!values.currencyId) return 'Elegí una moneda';
    if (values.items.length === 0) return 'Agregá al menos un ítem';
    for (const [i, item] of values.items.entries()) {
      if (!item.productId) return `Ítem ${i + 1}: elegí un producto`;
      const qty = Number(item.quantity);
      if (!Number.isFinite(qty) || qty <= 0)
        return `Ítem ${i + 1}: la cantidad tiene que ser positiva`;
      const price = Number(item.unitPrice);
      if (!Number.isFinite(price) || price < 0)
        return `Ítem ${i + 1}: el precio unitario no puede ser negativo`;
    }
    if (values.isFinanced) {
      if (!values.totalInstallments || values.totalInstallments < 1)
        return 'Las cotizaciones financiadas necesitan una cantidad positiva de cuotas';
      if (!values.startDate) return 'Las cotizaciones financiadas necesitan una fecha de inicio';
      const dp = Number(values.downPayment || 0);
      if (!Number.isFinite(dp) || dp < 0) return 'El pago inicial no puede ser negativo';
    }
    return null;
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    try {
      await create.mutateAsync(toWireValues(values));
      setValues(DEFAULT_VALUES);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear la cotización');
    }
  };

  const subtotal = values.items.reduce((acc, it) => acc + Number(it.subtotal || 0), 0);
  // 16% IVA Venezuela — see packages/shared-types/src/tax.ts for the
  // reconciliation plan once the business collapses the two rates.
  const tax = values.includesTax ? Number(IVA_RATE) * subtotal : 0;
  const total = subtotal + tax;

  // Pre-fill resolution: when the drawer opens with `initialValues`
  // carrying a `clientId` but no `clientName`, fetch the client
  // record so the input shows the resolved label instead of an
  // empty field. The early-out guard (clientName === '') prevents
  // the effect from firing again after we set the name once.
  const resolvedClient = useClient(values.clientId || null);
  useEffect(() => {
    const name = resolvedClient.data?.name;
    if (values.clientId && name && values.clientName === '') {
      setValues((prev) => ({ ...prev, clientName: name }));
    }
  }, [values.clientId, values.clientName, resolvedClient.data?.name]);

  // Current-user defaults. A SELLER sees no seller picker — the
  // drawer auto-fills their id and hides the field entirely so the
  // form is one step shorter. An ADMIN still gets the picker (with
  // themselves added as an option) so they can author quotes on
  // behalf of any seller or themselves.
  const currentUser = useCurrentUser();
  const isCurrentSeller = currentUser?.role === 'SELLER';
  const isCurrentAdmin = currentUser?.role === 'ADMIN';
  const defaultSellerId = isCurrentSeller ? (currentUser?.id ?? '') : '';

  // Seed `sellerId` on first paint when the drawer opens without a
  // pre-fill (CalculatorModal flow passes an `initialValues` object
  // that may or may not carry a sellerId — only seed when missing).
  // The `values.sellerId` dep short-circuits on every user-typed
  // selection because the inner guard `!values.sellerId` is false.
  useEffect(() => {
    if (open && !values.sellerId && defaultSellerId) {
      setValues((prev) => ({ ...prev, sellerId: defaultSellerId }));
    }
  }, [open, defaultSellerId, values.sellerId]);

  // Monthly installment preview shown next to the financed switch so
  // the user gets instant feedback on the financing decision without
  // opening a separate calculator modal. The rate mirrors the Slice
  // 8 calculator's continuous curve (3% at 1 month → 5.5% at 120
  // months, linear between). At 7+ months the curve resolves to a
  // rate; below 7 the rate defaults to 3% so the UI never shows a
  // $0 installment for a tiny term.
  const previewInstallment = useMemo(() => {
    if (!values.isFinanced) return null;
    const months = values.totalInstallments ?? 0;
    if (months < 1) return null;
    const RATE_MIN = 0.03;
    const RATE_MAX = 0.055;
    const TERM_MIN = 1;
    const TERM_MAX = 120;
    const clamped = Math.min(Math.max(months, TERM_MIN), TERM_MAX);
    const monthlyRate =
      RATE_MIN + ((clamped - TERM_MIN) / (TERM_MAX - TERM_MIN)) * (RATE_MAX - RATE_MIN);
    const principal =
      Number(values.downPayment || 0) > 0 ? total - Number(values.downPayment) : total;
    if (principal <= 0) return null;
    const installment = (principal * monthlyRate) / (1 - (1 + monthlyRate) ** -clamped);
    return Number.isFinite(installment) ? installment : null;
  }, [values.isFinanced, values.totalInstallments, values.downPayment, total]);

  return (
    <FormDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Nueva cotización"
      isSubmitting={create.isPending}
      submitLabel="Crear cotización"
      onSubmit={handleSubmit}
      contentClassName="md:max-w-3xl"
    >
      <div className="grid gap-3 md:grid-cols-2">
        {/* LEFT COLUMN — inputs */}
        <div className="space-y-2.5">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-clientId`}>Cliente</Label>
            <ClientPicker
              id={`${uid}-clientId`}
              value={values.clientId}
              displayValue={values.clientName}
              onChange={(client) =>
                setValues((prev) => ({ ...prev, clientId: client.id, clientName: client.name }))
              }
              onClear={() => setValues((prev) => ({ ...prev, clientId: '', clientName: '' }))}
              placeholder="Buscar cliente por nombre…"
            />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {isCurrentSeller ? (
              <div className="space-y-1.5">
                <Label>Vendedor</Label>
                <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                  {currentUser?.profile
                    ? `${currentUser.profile.firstName} ${currentUser.profile.lastName}`.trim() ||
                      currentUser.email
                    : currentUser?.email}
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-sellerId`}>Vendedor</Label>
                <SellerSelect
                  id={`${uid}-sellerId`}
                  value={values.sellerId}
                  onChange={(next) => setValues((prev) => ({ ...prev, sellerId: next }))}
                  placeholder="Elegí un vendedor"
                  {...(isCurrentAdmin ? { includeAdmin: true } : {})}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor={`${uid}-currencyId`}>Moneda</Label>
              <CurrencySelect
                id={`${uid}-currencyId`}
                value={values.currencyId}
                onChange={(next) => setValues((prev) => ({ ...prev, currencyId: next }))}
                placeholder="Elegí una moneda"
                codes={['USD', 'VES']}
              />
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={`${uid}-validUntil`}>Válido hasta (opcional)</Label>
              <Input
                id={`${uid}-validUntil`}
                name="validUntil"
                type="date"
                value={values.validUntil ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setValues((prev) => ({
                    ...prev,
                    validUntil: e.target.value || null,
                  }))
                }
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border p-3 shadow-sm">
              <Label htmlFor={`${uid}-includesTax`}>Impuestos 16%</Label>
              <Switch
                id={`${uid}-includesTax`}
                checked={values.includesTax}
                onCheckedChange={(checked) => setValues((prev) => ({ ...prev, includesTax: checked }))}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border p-3 shadow-sm">
            <Label htmlFor={`${uid}-isFinanced`}>Financiada</Label>
            <Switch
              id={`${uid}-isFinanced`}
              checked={values.isFinanced}
              onCheckedChange={(checked) =>
                setValues((prev) => ({
                  ...prev,
                  isFinanced: checked,
                  ...(checked
                    ? {}
                    : {
                        totalInstallments: undefined,
                        downPayment: '',
                        startDate: null,
                      }),
                }))
              }
            />
          </div>
          {values.isFinanced ? (
            <div className="grid gap-3 rounded-md border bg-muted/20 p-3 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-totalInstallments`}>Cuotas</Label>
                <Input
                  id={`${uid}-totalInstallments`}
                  name="totalInstallments"
                  type="number"
                  min={1}
                  value={values.totalInstallments ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValues((prev) => ({
                      ...prev,
                      totalInstallments: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-downPayment`}>Pago inicial</Label>
                <Input
                  id={`${uid}-downPayment`}
                  name="downPayment"
                  type="number"
                  step="0.0001"
                  min={0}
                  value={values.downPayment ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValues((prev) => ({ ...prev, downPayment: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`${uid}-startDate`}>Fecha de inicio</Label>
                <Input
                  id={`${uid}-startDate`}
                  name="startDate"
                  type="date"
                  value={values.startDate ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValues((prev) => ({
                      ...prev,
                      startDate: e.target.value || null,
                    }))
                  }
                />
              </div>
              {previewInstallment !== null ? (
                <p className="md:col-span-3 text-sm text-muted-foreground">
                  Cuota mensual estimada:{' '}
                  <span className="font-mono">{previewInstallment.toFixed(2)}</span>
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* RIGHT COLUMN — items + summary + notes (sticky) */}
        <div className="space-y-2.5">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Ítems</Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="mr-1 h-3 w-3" />
                Agregar ítem
              </Button>
            </div>
            <div className="space-y-2">
              {values.items.map((item, idx) => (
                <QuotationItemRowFields
                  key={item._key}
                  uid={uid}
                  index={idx}
                  item={item}
                  canRemove={values.items.length > 1}
                  onChange={(patch) => setItem(idx, patch)}
                  onRemove={() => removeItem(idx)}
                />
              ))}
            </div>
          </div>
          <div className="space-y-1.5 rounded-md border bg-muted/30 p-3">
            <div className="flex items-center justify-between text-sm">
              <span>Subtotal</span>
              <span className="font-mono">{subtotal.toFixed(2)}</span>
            </div>
            {values.includesTax ? (
              <div className="flex items-center justify-between text-sm">
                <span>Impuesto (16%)</span>
                <span className="font-mono">{tax.toFixed(2)}</span>
              </div>
            ) : null}
            <div className="flex items-center justify-between border-t pt-2 font-semibold">
              <span>Total</span>
              <span className="font-mono">{total.toFixed(2)}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-notes`}>Notas (opcional)</Label>
            <Textarea
              id={`${uid}-notes`}
              name="notes"
              value={values.notes ?? ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setValues((prev) => ({
                  ...prev,
                  notes: e.target.value || null,
                }))
              }
              maxLength={2000}
              rows={2}
            />
          </div>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      {create.error ? (
        <p className="text-sm text-destructive" role="alert">
          {create.error.message}
        </p>
      ) : null}
    </FormDrawer>
  );
}

// ---------------------------------------------------------------------------
// Per-row block for a single quotation line item.
// ---------------------------------------------------------------------------

interface QuotationItemRowFieldsProps {
  uid: string;
  index: number;
  item: QuotationItemRow;
  canRemove: boolean;
  onChange: (
    patch: Partial<QuotationItemRow> | ((current: QuotationItemRow) => Partial<QuotationItemRow>),
  ) => void;
  onRemove: () => void;
}

function QuotationItemRowFields({
  uid,
  index,
  item,
  canRemove,
  onChange,
  onRemove,
}: QuotationItemRowFieldsProps): React.ReactElement {
  // If the form was opened with a pre-filled productId (Calculator
  // flow), resolve the human label so the picker shows the name.
  const resolvedProduct = useProduct(item.productId || null);
  useEffect(() => {
    if (item.productId && !item.productName && resolvedProduct.data) {
      onChange({ productName: resolvedProduct.data.name });
    }
  }, [item.productId, item.productName, resolvedProduct.data, onChange]);

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Ítem {index + 1}</span>
        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label={`Quitar ítem ${index + 1}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${uid}-productId-${index}`}>Producto</Label>
        <ProductPicker
          id={`${uid}-productId-${index}`}
          value={item.productId}
          displayValue={item.productName}
          onChange={(p) =>
            onChange((current) => ({
              productId: p.id,
              productName: p.name,
              // Auto-fill the unit price from the product's catalog
              // `defaultSalePrice` ONLY when the current price is the
              // form-default ('0' or empty). Re-picking a product
              // after the user has manually edited the price keeps
              // their override. The CalculatorModal pre-fill sets a
              // non-zero price, so its values pass through unchanged.
              unitPrice:
                current.unitPrice && Number(current.unitPrice) > 0
                  ? current.unitPrice
                  : p.defaultSalePrice,
            }))
          }
          onClear={() => onChange({ productId: '', productName: '' })}
          placeholder="Buscar producto por nombre o SKU…"
        />
      </div>

      <div className="grid gap-2 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor={`${uid}-quantity-${index}`}>Cantidad</Label>
          <Input
            id={`${uid}-quantity-${index}`}
            name={`quantity-${index}`}
            type="number"
            step="0.0001"
            min={0.0001}
            value={item.quantity}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ quantity: e.target.value })
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${uid}-unitPrice-${index}`}>Precio unitario</Label>
          <Input
            id={`${uid}-unitPrice-${index}`}
            name={`unitPrice-${index}`}
            type="number"
            step="0.0001"
            min={0}
            value={item.unitPrice}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ unitPrice: e.target.value })
            }
          />
        </div>
        <div className="space-y-2">
          <Label>Subtotal</Label>
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm font-mono">
            {Number(item.subtotal || 0).toFixed(2)}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${uid}-notes-${index}`}>Notas (opcional)</Label>
        <Textarea
          id={`${uid}-notes-${index}`}
          name={`notes-${index}`}
          value={item.notes ?? ''}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange({ notes: e.target.value || null })
          }
          maxLength={500}
        />
      </div>
    </div>
  );
}
